import { MultiStepForm } from '@nw/fields-manager';
import {
  ApplicableDiscountsType,
  ErrorMessageObjectType,
  GetProductDetailsResponseType,
  InstallmentPricingDetailsType,
  MultiStepFormFieldsDataType,
  PreviousProductDetailsType,
  ProductExtraDataType,
  ProductPurchaseDetailsType,
  ProductSegmentConfigType,
  RegistrationMessagesConfigType
} from '@nw/shared/data-access';

import { transformUserExtraInfoConfig } from '../transformers/FieldDataTransformer';

import RegisteredProductForChangeProgram from './RegisteredProductForChangeProgram';

export abstract class BaseProductDetail {
  // Basic product info
  readonly productCode: string;
  readonly isActive: boolean;
  readonly productInfo?: string;

  // Program info
  readonly program: string;
  readonly programExtraInfo: string;
  readonly programInfo?: string;
  readonly programConfig?: string;
  readonly programImageUrl?: string;
  readonly programSubscriptionUrl?: string;

  // Product configuration
  readonly currency: string;
  readonly dependentProductCode?: string;
  readonly dependentProductCodeCondition: 'OR' | 'AND';
  readonly allowedNoOfPurchases: number;
  readonly tncToAccept: string[];

  // Amounts
  readonly originalAmountInLeastSubunit: number;
  readonly originalAmount: number;
  readonly productPrice: number;

  // Product details object
  readonly pricingType: string;
  readonly installmentPricingDetails?: InstallmentPricingDetailsType;
  readonly applicableDiscounts?: ApplicableDiscountsType;

  // Product purchase details for user (entire object)
  readonly productPurchaseDetailsForUser: ProductPurchaseDetailsType;

  // Parsed JSON fields
  readonly parsedExtraData: ProductExtraDataType;
  readonly parsedRegistrationConfig: RegistrationMessagesConfigType;
  readonly parsedProductConfig: ProductSegmentConfigType;
  readonly parsedUserExtraInfoConfig?: MultiStepFormFieldsDataType;
  readonly parsedErrorMessagesConfig: ErrorMessageObjectType;

  //change program details
  readonly registeredProductsForChangeProgram: RegisteredProductForChangeProgram[];
  readonly totalPaidAmount: number | undefined;
  readonly amountToBeRefunded: number | undefined;

  form?: MultiStepForm;

  protected readonly rawProductDetails: GetProductDetailsResponseType;

  constructor(productDetails: GetProductDetailsResponseType) {
    this.rawProductDetails = productDetails;

    // Basic product info
    this.productCode = productDetails.product_code;
    this.isActive = productDetails.is_active;
    this.productInfo = productDetails.product_info;

    // Program info
    this.program = productDetails.program;
    this.programExtraInfo = productDetails.program_extra_info;
    this.programInfo = productDetails.program_info;
    this.programConfig = productDetails.program_config;
    this.programImageUrl = productDetails.program_image_url;
    this.programSubscriptionUrl = productDetails.program_subscription_url;

    // Product configuration
    this.currency = productDetails.currency;
    this.dependentProductCode = productDetails.dependent_product_code;
    this.dependentProductCodeCondition =
      productDetails.dependent_product_code_condition;
    this.allowedNoOfPurchases = productDetails.allowed_no_of_purchases;
    this.tncToAccept = productDetails.tnc_to_accept;

    // Amounts
    this.originalAmountInLeastSubunit =
      productDetails.original_amount_in_least_subunit;
    this.originalAmount = productDetails.original_amount;
    this.productPrice = productDetails.product_details.product_price;

    // Product details object
    this.pricingType = productDetails.product_details.pricing_type;
    this.installmentPricingDetails =
      productDetails.product_details.installment_pricing_details;
    this.applicableDiscounts =
      productDetails.product_details.applicable_discounts;

    // Product purchase details for user (entire object)
    this.productPurchaseDetailsForUser =
      productDetails.product_purchase_details_for_user;

    // Parse all JSON strings
    this.parsedExtraData = this.parseJson<ProductExtraDataType>(
      productDetails.extra_data,
      {} as ProductExtraDataType
    );
    this.parsedRegistrationConfig =
      this.parseJson<RegistrationMessagesConfigType>(
        productDetails.registration_messages_config,
        {} as RegistrationMessagesConfigType
      );
    this.parsedProductConfig = this.parseJson<ProductSegmentConfigType>(
      productDetails.product_config,
      {} as ProductSegmentConfigType
    );
    this.parsedUserExtraInfoConfig = productDetails.user_extra_info_config
      ? this.parseUserExtraInfoConfig(productDetails.user_extra_info_config)
      : undefined;
    this.parsedErrorMessagesConfig = this.parseJson<ErrorMessageObjectType>(
      productDetails.error_messages_config,
      {} as ErrorMessageObjectType
    );

    //change program details
    this.registeredProductsForChangeProgram =
      this.getRegisteredProductsForChangeProgram(
        productDetails.product_purchase_details_for_user.change_program_details
          ?.previous_products_details
      );
    this.totalPaidAmount = this.getTotalPaidAmount(
      productDetails.product_purchase_details_for_user.change_program_details
        ?.previous_products_details
    );
    this.amountToBeRefunded =
      productDetails.product_purchase_details_for_user.change_program_details?.refund_details?.refund_amount;
  }

  abstract shouldShowCouponApplyButton(): boolean;

  private parseJson<T>(jsonString?: string, defaultValue: T = {} as T): T {
    if (!jsonString) return defaultValue;
    try {
      return JSON.parse(jsonString) as T;
    } catch {
      return defaultValue;
    }
  }

  private parseUserExtraInfoConfig(
    jsonString: string
  ): MultiStepFormFieldsDataType {
    try {
      const parsed = JSON.parse(jsonString);
      return transformUserExtraInfoConfig(
        parsed
      ) as MultiStepFormFieldsDataType;
    } catch {
      // Return default structure on parse error
      return transformUserExtraInfoConfig(null) as MultiStepFormFieldsDataType;
    }
  }

  getFormInitialValues() {
    const initialValues = this.form?.allFormFields.reduce(
      (acc, field) => {
        acc[field.field] = field.initialValue ?? '';
        return acc;
      },
      {} as Record<string, string>
    );
    return initialValues;
  }

  // Helper methods for common access patterns
  getApplicableDiscountsByType(discountType: string) {
    return (
      this.applicableDiscounts?.filter(
        (discount) => discount.discount_type === discountType
      ) || []
    );
  }

  hasChangeProgram(): boolean {
    return !!this.productPurchaseDetailsForUser?.change_program_details;
  }

  getRefundAmount(): number {
    return (
      this.productPurchaseDetailsForUser?.change_program_details?.refund_details
        ?.refund_amount || 0
    );
  }

  getPreviousProductsDetails() {
    return (
      this.productPurchaseDetailsForUser?.change_program_details
        ?.previous_products_details || []
    );
  }

  getTotalAmountToBePaid(): number {
    return (
      this.productPurchaseDetailsForUser?.pricing_details
        ?.total_amount_to_be_paid || 0
    );
  }

  getTotalAmountToBePayedInLeastSubunit(): number {
    return (
      this.productPurchaseDetailsForUser?.pricing_details
        ?.total_amount_to_be_paid_in_least_subunit || 0
    );
  }

  getRegisteredProductsForChangeProgram(
    registeredProducts: PreviousProductDetailsType[] | undefined | null
  ) {
    if (registeredProducts)
      return registeredProducts.map(
        (eachProduct) => new RegisteredProductForChangeProgram(eachProduct)
      );
    return [];
  }

  getTotalPaidAmount(
    paidProductDetails: PreviousProductDetailsType[] | undefined | null
  ): number {
    if (!paidProductDetails) return 0;
    return paidProductDetails.reduce(
      (acc, eachProduct) => acc + eachProduct.amount_paid_by_user,
      0
    );
  }

  hasUserPaidAtLeastOneProductOtherThanAdvanceProductForChangeProgram(): boolean {
    return this.registeredProductsForChangeProgram.some(
      (eachProduct) => eachProduct.isAdvanceProduct === false
    );
  }

  hasUserPaidOnlyAdvanceProductsForChangeProgram(): boolean {
    return !this.hasUserPaidAtLeastOneProductOtherThanAdvanceProductForChangeProgram();
  }

  getAdvanceProductsForChangeProgram(): RegisteredProductForChangeProgram[] {
    return this.registeredProductsForChangeProgram.filter(
      (eachProduct) => eachProduct.isAdvanceProduct === true
    );
  }

  get totalReservedAmount(): number {
    return this.getAdvanceProductsForChangeProgram().reduce(
      (acc, eachProduct) => acc + eachProduct.amountPaidByUser,
      0
    );
  }

  get changeProgramAlreadyRegisteredProductsString(): string {
    return this.registeredProductsForChangeProgram
      .map((eachProduct) => eachProduct.productDisplayName)
      .join(',');
  }

  getIsEligibleForRefund(): boolean {
    return !!this.amountToBeRefunded && this.amountToBeRefunded > 0;
  }

  getOtherThanAdvanceRegisteredProductsForChangeProgramDisplayList(): RegisteredProductForChangeProgram[] {
    return this.registeredProductsForChangeProgram.filter(
      (eachProduct) => eachProduct.isAdvanceProduct === false
    );
  }

  getOtherThanAdvanceRegisteredProductsForChangeProgramDisplayListString(): string {
    return this.getOtherThanAdvanceRegisteredProductsForChangeProgramDisplayList()
      .map((eachProduct) => eachProduct.productDisplayName)
      .join(',');
  }
}
